import { useContext, useEffect, useRef, useState } from "react";
import { prefersReducedMotion, ScrollerContext } from "../RoyalMahal/hooks";
import "./ScrollCue.scss";

// Gentle at both ends
const easeInOut = (t) => 0.5 - Math.cos(Math.PI * t) / 2;
// Any of these from the guest takes the scrolling back from a glide
const TAKEOVER = ["wheel", "touchstart", "pointerdown", "keydown"];

/**
 * Scrolls `scroller` (an element, or the window) to `to` over `duration` ms, frame by
 * frame, so scroll-driven animations on the way play at an easy pace. Returns a function
 * that stops it; the guest scrolling or tapping stops it too.
 */
const glide = (scroller, to, duration) => {
  const isWindow = scroller === window;
  const from = isWindow ? window.scrollY : scroller.scrollTop;
  const distance = to - from;
  let frame = 0;
  let start = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    TAKEOVER.forEach((type) => window.removeEventListener(type, stop, true));
  };
  const step = (now) => {
    if (!start) start = now;
    const t = Math.min(1, (now - start) / duration);
    const y = from + distance * easeInOut(t);
    if (isWindow) window.scrollTo(0, y);
    else scroller.scrollTop = y;
    if (t < 1) frame = requestAnimationFrame(step);
    else stop();
  };
  if (Math.abs(distance) < 1) return stop;
  TAKEOVER.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
  frame = requestAnimationFrame(step);
  return stop;
};

/**
 * "Scroll down" prompt pinned to the foot of the screen on a theme's first page. It
 * fades in after a short delay and leaves for good on the guest's first scroll; tapping
 * it nudges the page down for anyone who taps rather than scrolls.
 *
 * Shared by the themes. It scrolls the theme's own scroller (ScrollerContext), or the
 * window when there is none. Each theme gives it its colours, typeface and timing with
 * the --scroll-cue-* custom properties listed in ScrollCue.scss. Rendered inside a
 * theme's hero, it also waits with the hero's other animations (e.g. behind a gate).
 *
 * By default a tap scrolls most of a screen down with the browser's smooth scrolling.
 * A theme can instead have it glide slowly (`glideMs`) to a place of its choosing
 * (`scrollTarget(scroller)`, a scroll position), so that scroll-driven animations on
 * the way can be seen. With reduced motion it jumps there.
 */
const ScrollCue = ({ label = "Scroll down", className = "", scrollTarget, glideMs }) => {
  const scrollerRef = useContext(ScrollerContext);
  const [gone, setGone] = useState(false);
  const stopGlide = useRef(null);

  useEffect(() => () => stopGlide.current?.(), []);

  useEffect(() => {
    if (gone) return undefined;
    const scroller = scrollerRef?.current || window;
    const scrolled = () => (scroller === window ? window.scrollY : scroller.scrollTop);
    const onScroll = () => {
      if (scrolled() > 8) setGone(true);
    };
    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [scrollerRef, gone]);

  const nudge = () => {
    const scroller = scrollerRef?.current || window;
    const reduced = prefersReducedMotion();
    if (!scrollTarget && !glideMs) {
      scroller.scrollBy({ top: window.innerHeight * 0.8, behavior: reduced ? "auto" : "smooth" });
      return;
    }
    const current = scroller === window ? window.scrollY : scroller.scrollTop;
    const target = scrollTarget?.(scrollerRef?.current || document.scrollingElement);
    const to = typeof target === "number" ? target : current + window.innerHeight * 0.8;
    stopGlide.current?.();
    if (reduced || !glideMs) {
      scroller.scrollTo({ top: to, behavior: reduced ? "auto" : "smooth" });
      return;
    }
    stopGlide.current = glide(scroller, to, glideMs);
  };

  return (
    <button
      type="button"
      className={`scroll-cue ${gone ? "is-gone" : ""} ${className}`}
      onClick={nudge}
      tabIndex={gone ? -1 : 0}
      aria-hidden={gone}
    >
      <span className="scroll-cue__inner">
        <span className="scroll-cue__label">{label}</span>
        <svg className="scroll-cue__arrows" viewBox="0 0 24 22" aria-hidden="true">
          <path d="M5 3l7 6 7-6" />
          <path d="M5 12l7 6 7-6" />
        </svg>
      </span>
    </button>
  );
};

export default ScrollCue;
